import React, { useEffect, useState } from 'react';

import { ReactComponent as AvailableIcon } from '../../../../assets/device-hub/dasnboard/available.svg';
import { ReactComponent as BlockedDeviceIcon } from '../../../../assets/device-hub/dasnboard/blocked-device.svg';
import { ReactComponent as ChargingIcon } from '../../../../assets/device-hub/dasnboard/charging.svg';
import { ReactComponent as FinishingIcon } from '../../../../assets/device-hub/dasnboard/finishing.svg';
import { ReactComponent as OnlineDeviceIcon } from '../../../../assets/device-hub/dasnboard/online-device.svg';
import { ReactComponent as PreparingIcon } from '../../../../assets/device-hub/dasnboard/preparing.svg';
import { ReactComponent as ReservedIcon } from '../../../../assets/device-hub/dasnboard/reserved.svg';
import { ReactComponent as SuspendedEventIcon } from '../../../../assets/device-hub/dasnboard/suspendedEV.svg';
import { ReactComponent as SuspendedEvseIcon } from '../../../../assets/device-hub/dasnboard/suspendedEVSE.svg';
import { ReactComponent as TotalConnectorIcon } from '../../../../assets/device-hub/dasnboard/total-connecter.svg';
import { ReactComponent as DashDeviceIcon } from '../../../../assets/device-hub/dasnboard/total-device.svg';
import { ReactComponent as UnknownIcon } from '../../../../assets/device-hub/dasnboard/unknow.svg';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { updateDeviceFilters } from '../../../../redux/slices/Partners/DeviceManagementSlice';
import { setActiveTabKey } from '../../../../redux/slices/StationManagementSidePanelSlice';
import stationApi from '../../../../utils/api/station-management';

/**
 * Renders Device Quick Info (top) and Connector Quick Info (bottom) cards
 * @returns {any} JSX element
 */
function DashboardWidgets() {
  const dispatch = useAppDispatch();
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const [deviceStats, setDeviceStats] = useState({
    totalDevices: 0,
    onlineDeviceCount: 0,
    offlineDeviceCount: 0,
    availableDeviceCount: 0,
    unavailableDeviceCount: 0,
  });

  const [connectorStats, setConnectorStats] = useState({
    total: 0,
    available: 0,
    preparing: 0,
    charging: 0,
    finishing: 0,
    faulted: 0,
    suspendedEv: 0,
    suspendedEvse: 0,
    reservations: 0,
    unavailable: 0,
    unknown: 0,
  });

  const [loading, setLoading] = useState(false);

  /**
   * Handles click events on device status widgets to filter by status and switch tabs.
   * @param {string} status - The selected device status filter value.
   */
  const handleDeviceStatusClick = (status: string) => {
    if (status === 'total') {
      dispatch(
        updateDeviceFilters({
          key: 'deviceStatus',
          value: '',
          type: 'remove',
        }),
      );
    } else {
      dispatch(
        updateDeviceFilters({
          key: 'deviceStatus',
          value: status,
          type: 'restAndAdd',
        }),
      );
    }
    dispatch(setActiveTabKey('chargers'));
  };

  /**
   * Handles click events on connector status widgets to filter by status and switch tabs.
   * @param {string} status - The selected connector status filter value.
   */
  const handleConnectorStatusClick = (status: string) => {
    if (status === 'total') {
      dispatch(
        updateDeviceFilters({
          key: 'connectorStatus',
          value: '',
          type: 'remove',
        }),
      );
    } else {
      dispatch(
        updateDeviceFilters({
          key: 'connectorStatus',
          value: status,
          type: 'restAndAdd',
        }),
      );
    }
    dispatch(setActiveTabKey('chargers'));
  };

  useEffect(() => {
    /**
     * Fetches device and connector status count statistics for the selected charging station
     */
    const fetchWidgetData = async () => {
      if (!selectedChargingPoint?.id) return;
      setLoading(true);
      try {
        const [deviceResponse, connectorResponse] = await Promise.all([
          stationApi.getStationDeviceStatusCount({ chargingStation: selectedChargingPoint.id }),
          stationApi.getDeviceConnectorStatus({ chargingStation: selectedChargingPoint.id }),
        ]);

        if (deviceResponse?.status === 200 || deviceResponse?.data) {
          const deviceData = deviceResponse.data || deviceResponse;
          setDeviceStats({
            totalDevices: deviceData.total ?? 0,
            onlineDeviceCount: deviceData.online ?? 0,
            offlineDeviceCount: deviceData.offline ?? 0,
            availableDeviceCount: deviceData.available ?? 0,
            unavailableDeviceCount: deviceData.unavailable ?? 0,
          });
        }

        if (connectorResponse?.status === 200 || connectorResponse?.data) {
          const connectorData = connectorResponse.data || connectorResponse;
          const counts = connectorData.statusCounts || [];

          const mappedStats = {
            total: connectorData.total ?? 0,
            available: 0,
            preparing: 0,
            charging: 0,
            finishing: 0,
            faulted: 0,
            suspendedEv: 0,
            suspendedEvse: 0,
            reservations: 0,
            unavailable: 0,
            unknown: 0,
          };

          counts.forEach((item: any) => {
            const status = item.status?.toLowerCase();
            const count = item.count ?? 0;
            switch (status) {
              case 'available':
                mappedStats.available = count;
                break;
              case 'preparing':
                mappedStats.preparing = count;
                break;
              case 'charging':
                mappedStats.charging = count;
                break;
              case 'finishing':
                mappedStats.finishing = count;
                break;
              case 'faulted':
                mappedStats.faulted = count;
                break;
              case 'suspendedev':
                mappedStats.suspendedEv = count;
                break;
              case 'suspendedevse':
                mappedStats.suspendedEvse = count;
                break;
              case 'reserved':
                mappedStats.reservations = count;
                break;
              case 'unavailable':
                mappedStats.unavailable = count;
                break;
              case 'unknown':
                mappedStats.unknown = count;
                break;
              default:
                break;
            }
          });
          setConnectorStats(mappedStats);
        }
      } catch (error) {
        console.error('Error fetching dashboard widget data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWidgetData();
  }, [selectedChargingPoint?.id]);

  if (loading) {
    return (
      <div className="skeleton-grid-loader">
        <div className="skeleton-card" />
      </div>
    );
  }

  return (
    <div className="station-quick-info-wrapper-charger">
      {/* ── Device Quick Info – TOP ── */}
      <div className="quick-info-card">
        <div className="card-header">
          <h4>Device Quick Info</h4>
          <span
            className="info-icon-tooltip"
            data-tooltip="Provides a high-level overview of EV charger devices registered in the platform, including operational, connectivity, and assignment status.">
            <InfoIcon />
          </span>
        </div>
        <div className="card-grid device-grid">
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleDeviceStatusClick('total')}>
            <div className="stat-info">
              <span className="stat-label label--teal">Total</span>
              <span className="stat-value">{deviceStats.totalDevices || 0}</span>
            </div>
            <div className="stat-icon icon--gray">
              <DashDeviceIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleDeviceStatusClick('online')}>
            <div className="stat-info">
              <span className="stat-label label--green">Online</span>
              <span className="stat-value">{deviceStats?.onlineDeviceCount ?? 0}</span>
            </div>
            <div className="stat-icon icon--green">
              <OnlineDeviceIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleDeviceStatusClick('offline')}>
            <div className="stat-info">
              <span className="stat-label label--red">Offline</span>
              <span className="stat-value">{deviceStats?.offlineDeviceCount ?? 0}</span>
            </div>
            <div className="stat-icon icon--faulted">
              <BlockedDeviceIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleDeviceStatusClick('available')}>
            <div className="stat-info">
              <span className="stat-label label--green">Available</span>
              <span className="stat-value">{deviceStats?.availableDeviceCount ?? 0}</span>
            </div>
            <div className="stat-icon icon--available">
              <AvailableIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleDeviceStatusClick('unavailable')}>
            <div className="stat-info">
              <span className="stat-label label--coolgray">Unavailable</span>
              <span className="stat-value">{deviceStats?.unavailableDeviceCount ?? 0}</span>
            </div>
            <div className="stat-icon icon--unavailable">
              <UnknownIcon />
            </div>
          </div>
        </div>
      </div>

      {/* ── Connector Quick Info – BOTTOM ── */}
      <div className="quick-info-card">
        <div className="card-header">
          <h4>Connector Quick Info</h4>
          <span
            className="info-icon-tooltip"
            data-tooltip="Provides a real-time summary of connector availability and charging session states across all registered EV chargers.">
            <InfoIcon />
          </span>
        </div>
        <div className="card-grid connector-grid">
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('total')}>
            <div className="stat-info">
              <span className="stat-label label--purple">Total</span>
              <span className="stat-value">{connectorStats?.total ?? 0}</span>
            </div>
            <div className="stat-icon icon--purple">
              <TotalConnectorIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('Available')}>
            <div className="stat-info">
              <span className="stat-label label--green">Available</span>
              <span className="stat-value">{connectorStats?.available ?? 0}</span>
            </div>
            <div className="stat-icon icon--available">
              <AvailableIcon  />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('Preparing')}>
            <div className="stat-info">
              <span className="stat-label label--amber">Preparing</span>
              <span className="stat-value">{connectorStats?.preparing ?? 0}</span>
            </div>
            <div className="stat-icon icon--preparing">
              <PreparingIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('Charging')}>
            <div className="stat-info">
              <span className="stat-label label--blue">Charging</span>
              <span className="stat-value">{connectorStats?.charging ?? 0}</span>
            </div>
            <div className="stat-icon icon--charging">
              <ChargingIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('Finishing')}>
            <div className="stat-info">
              <span className="stat-label label--teal">Finishing</span>
              <span className="stat-value">{connectorStats?.finishing ?? 0}</span>
            </div>
            <div className="stat-icon icon--finishing">
              <FinishingIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('Faulted')}>
            <div className="stat-info">
              <span className="stat-label label--red">Faulted</span>
              <span className="stat-value">{connectorStats?.faulted ?? 0}</span>
            </div>
            <div className="stat-icon icon--faulted">
              <BlockedDeviceIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('SuspendedEV')}>
            <div className="stat-info">
              <span className="stat-label label--pink">SuspendedEV</span>
              <span className="stat-value">{connectorStats?.suspendedEv ?? 0}</span>
            </div>
            <div className="stat-icon icon--suspendedEV">
              <SuspendedEventIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('SuspendedEVSE')}>
            <div className="stat-info">
              <span className="stat-label label--darkorange">SuspendedEVSE</span>{' '}
              <span className="stat-value">{connectorStats?.suspendedEvse ?? 0}</span>
            </div>
            <div className="stat-icon icon--suspendedEVSE">
              <SuspendedEvseIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('Reserved')}>
            <div className="stat-info">
              <span className="stat-label label--cyan">Reserved</span>
              <span className="stat-value">{connectorStats?.reservations ?? 0}</span>
            </div>
            <div className="stat-icon icon--reservation">
              <ReservedIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('Unknown')}>
            <div className="stat-info">
              <span className="stat-label label--dark">Unknown</span>
              <span className="stat-value">{connectorStats?.unknown ?? 0}</span>
            </div>
            <div className="stat-icon icon--unknown">
              <UnknownIcon />
            </div>
          </div>
          <div
            className="stat-item"
            role="presentation"
            onClick={() => handleConnectorStatusClick('Unavailable')}>
            <div className="stat-info">
              <span className="stat-label label--coolgray">Unavailable</span>
              <span className="stat-value">{connectorStats?.unavailable ?? 0}</span>
            </div>
            <div className="stat-icon icon--unavailable">
              <UnknownIcon />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardWidgets;
